import { useEffect, createElement as rc, createContext, useState, useCallback } from 'react';
import useEventSink from '../../hooks/useEventSink';
import { localStorage } from 'lib_ui-services';
import { useTheme } from 'styled-components';

const SIDEBAR_LOCKED = 'sidebarLocked';
/**
 * Navbar Context.
 * Navbar has 3 states on Desktop, and 2 states on Mobile
 * Desktop:
 *  - Locked (+ Open) : Navbar is fully visible and locked open (takes up space from the main area)
 *  - !Locked + Open: Navbar is collapsed to icons, but temporarily expanded to show full content (overlays main area)
 *  - !Locked + !open: Navbar is collapsed to icons only
 *
 * Mobile (ignores Locked state):
 *  - Open: Navbar is fully visible (overlays content)
 *  - !Open: Navbar is hidden
 *
 *  Locked indicates the navbar takes up space in the layout (more than the icons).
 *  Open indicates whether the the full content is visible (not just icons).
 * @typedef NavBarContext
 * @property {boolean} open if full content is visible, or not. (not used on mobile))
 * @property {boolean} locked - Desktop - Whether the navbar is expanded (showing full content),
 *                              or collapsed to icons only (desktop)/hidden (mobile)
 * @property {boolean} ready - Whether the context is ready to be used
 * @property {function} setOpen - Function to set open state
 * @property {function} toggleOpen - Function to toggle open state
 * @property {function} setLocked - Function to set locked state
 * @property {function} toggleLockState - Function to toggle locked state *
 */
/**
 * @type {React.Context<NavBarContext>}
 */
export const NavbarContext = createContext({
    ready: false,
    open: false,
    locked: false,
    setOpen: () => {},
    toggleOpen: () => {},
    setLocked: () => {},
    toggleLockState: () => {}
});

/**
 * On Desktop:
 *  - Open: true: Navbar is fully visible /
 *
 * @param {*} props
 * @returns
 */
export default function Navbar(props) {
    const { mobile } = useTheme();
    const [locked, _setLocked] = useState(!mobile);
    const [open, setOpen] = useState(!mobile);
    const [ready, setReady] = useState(false);
    const [subscribe] = useEventSink();

    // Track whether the user likes the sidebar locked.
    const setLocked = useCallback(locked => {
        if (typeof locked === 'function') {
            _setLocked(isLocked => {
                let newLocked = locked(isLocked);
                localStorage.setKey(SIDEBAR_LOCKED, JSON.stringify(newLocked));
                return newLocked;
            });
        } else {
            localStorage.setKey(SIDEBAR_LOCKED, JSON.stringify(locked));
            _setLocked(locked);
        }
    }, []);

    // Recover user's sidebar locked preference
    useEffect(() => {
        (async () => {
            let locked = await localStorage.getKey(SIDEBAR_LOCKED, undefined, JSON.stringify(!mobile));
            locked = locked === 'true';
            _setLocked(locked);
            setOpen(locked);
            setReady(true);
        })();
    }, [mobile]);

    useEffect(() => {
        const unsubscribe = subscribe({ verb: 'navigate', namespace: 'application', relation: 'route' }, () => {
            if (!locked) setOpen(false);
        });
        return () => unsubscribe();
        // subscribing more than once serves no purpose and could create a memory leak.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subscribe]);

    const toggleOpen = () => setOpen(isOpen => !isOpen);
    const toggleLockState = () => {
        if (!locked) {
            setOpen(true);
            setLocked(true);
        } else {
            setOpen(false);
            setLocked(false);
        }
    };
    return rc(
        NavbarContext.Provider,
        { value: { ready, open: locked ? true : open, setOpen, toggleOpen, toggleLockState, locked, setLocked } },
        props.children
    );
}
